import type { IdTokenWithTrueNorthExtensions } from '#app/shared/types/AuthTypes'
import type { AuthenticatedUser } from '#app/shared/types/UserTypes'
import { authClaimsHasRole, ClaimRoles } from '#app/shared/utils/auth'
export const KeyAnonymousUser = 'anonymous'
export const ValueUnknown = 'unknown'
export const KeyUnknownUser = 'unknown'

export enum AuthUserType {
  Unknown = 'unknown',
  Anonymous = 'anonymous',
  Staff = 'staff',
  Driver = 'driver',
  CompanyAdmin = 'companyAdmin',
  Broker = 'broker',
  BrokerAdmin = 'brokerAdmin'
}

export enum AuthUserRelationship {
  None = 'none',
  /** User is associated with a carrier. */
  Carrier = 'carrier',
  /** User is associated with a broker. */
  Broker = 'broker'
}
/**
 * Authentication context to use for external services (e.g. LaunchDarkly, FullStory, etc)
 */
export type AuthContextExternal = {
  email: string
  userType: AuthUserType
  isApproved?: boolean
  isSuperUser?: boolean
  isMco?: boolean
  isSelfDispatch?: boolean
}

export type AuthContextResult = {
  authenticated: boolean
  anonymous: boolean
  approved: boolean | null
  /** Authenticated user returned from /api/authenticated-user */
  authenticatedUser: AuthenticatedUser | null
  userKey: string
  userEmail: string | null
  userFirstName: string | null
  userLastName: string | null
  userFullName: string | null
  userType: AuthUserType
  userRelationship: AuthUserRelationship
  externalContext?: AuthContextExternal
}

export const InitializingAuthContext: AuthContextResult = {
  authenticated: false,
  anonymous: true,
  approved: null,
  authenticatedUser: null,
  userKey: KeyUnknownUser,
  userEmail: null,
  userFirstName: null,
  userLastName: null,
  userFullName: ValueUnknown,
  userType: AuthUserType.Unknown,
  userRelationship: AuthUserRelationship.None,
  externalContext: {
    email: ValueUnknown,
    userType: AuthUserType.Unknown
  }
}

export const AnonymousAuthContext: AuthContextResult = {
  authenticated: false,
  anonymous: true,
  approved: null,
  authenticatedUser: null,
  userKey: KeyAnonymousUser,
  userFirstName: null,
  userLastName: null,
  userFullName: KeyAnonymousUser,
  userEmail: null,
  userType: AuthUserType.Anonymous,
  userRelationship: AuthUserRelationship.None,
  externalContext: {
    email: KeyAnonymousUser,
    userType: AuthUserType.Anonymous
  }
}

export const getAuthContextForUser = (
  userIdToken?: IdTokenWithTrueNorthExtensions | null,
  authenticatedUser: AuthenticatedUser | null = null
): AuthContextResult => {
  // must have an id token and be authenticated to be considered authenticated
  const authenticated = !!userIdToken && authenticatedUser?.is_authenticated

  if (!authenticated) {
    return AnonymousAuthContext
  }

  let userType = AuthUserType.Unknown
  const userEmail = userIdToken.email ?? ValueUnknown
  const isApproved = authenticatedUser.is_approved ?? undefined

  if (
    authClaimsHasRole(userIdToken, ClaimRoles.Staff)
    || authenticatedUser.is_staff
  ) {
    userType = AuthUserType.Staff
  } else if (authClaimsHasRole(userIdToken, ClaimRoles.Broker)) {
    userType = AuthUserType.Broker
  } else if (authClaimsHasRole(userIdToken, ClaimRoles.BrokerAdmin)) {
    userType = AuthUserType.BrokerAdmin
  } else if (authenticatedUser.is_company_admin) {
    userType = AuthUserType.CompanyAdmin
  } else if (authenticatedUser.is_trucker) {
    userType = AuthUserType.Driver
  }

  const externalContext: AuthContextExternal = {
    email: userEmail,
    userType,
    isApproved,
    isSuperUser: authenticatedUser.is_superuser ?? false,
    isMco: authenticatedUser.is_mco ?? false,
    isSelfDispatch: authenticatedUser.is_self_dispatch ?? false
  }

  const {
    first_name,
    last_name,
    id: userId
  } = authenticatedUser ?? { first_name: null, id: null }
  const hasAuthFirstName = !!first_name
  const userFirstName = hasAuthFirstName ? first_name : userIdToken.given_name
  const userLastName = hasAuthFirstName ? last_name : userIdToken.family_name
  const userFullName = userFirstName
    ? `${userFirstName} ${userLastName}`
    : userEmail ?? ValueUnknown

  const userIdString = userId?.toString() || `FALLBACK:${userEmail}` // fallback to email if no user id
  let userRelationship = AuthUserRelationship.None

  if (authenticatedUser.carrier_id || authenticatedUser.company_id) {
    userRelationship = AuthUserRelationship.Carrier
  } else if (authenticatedUser.broker_id) {
    userRelationship = AuthUserRelationship.Broker
  }

  return {
    authenticated,
    anonymous: authenticated !== true,
    approved: isApproved ?? null,
    authenticatedUser,
    userKey: userIdString,
    userEmail,
    userFirstName: userFirstName || ValueUnknown,
    userLastName: userLastName || ValueUnknown,
    userFullName,
    userType,
    userRelationship,
    externalContext
  }
}
